import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Button, Divider, List, MenuItem } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AllIcon from 'app/assets/images/Tasks/nav-all-tasks.svg';
import CandidateIcon from 'app/assets/images/Tasks/nav-candidate-tasks.svg';
import FacilityIcon from 'app/assets/images/Tasks/nav-facility-tasks.svg';
import OrderIcon from 'app/assets/images/Tasks/nav-order-tasks.svg';
import PlacementIcon from 'app/assets/images/Tasks/nav-placement-tasks.svg';
import { TaskEntity } from 'app/models/Tasks/Tasks';
import { colors } from 'styles/styleVariables';
import { taskDetailsActions } from '../store/Tasks.redux';
import { selectTaskEntity } from '../store/Tasks.selectors';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { selectUser } from 'oidc/user.selectors';
import { defaultTaskFilters } from './TaskFilters/ITaskFilter';
import { useFormContext } from 'react-hook-form';
import { RootState } from 'types';
import {
  convertArrayToKeyValuePairs,
  convertSelectedFilterValue,
  formatSelectedFilter,
} from './TaskFilters/TaskFilterUtilitis';

const useStyles = makeStyles()(theme => ({
  btn: {
    width: '100%',
    maxWidth: '185px',
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    backgroundColor: colors.amnBlue,
    '& .MuiButton-endIcon': {
      right: '10px',
    },
    '& .MuiButton-label': {
      justifyContent: 'flex-start',
    },
    height: '28px',
  },
  label: {
    color: '#B9BBBC',
    padding: '10px 10px',
    textAlign: 'left',
  },
  menuList: {
    padding: 0,
    width: '200px',
    minWidth: '120px',
    position: 'absolute',
   
    right: 0,
    zIndex: 100,
    top: '40px',
    backgroundColor: '#FFFFFF',
    borderRadius: '4px',
    boxShadow: '0px 2px 8px #0000004D',
    fontSize: '14px',
    color: colors.darkText,
    '& .MuiMenuItem-root': {
      padding: '10px 16px',
      height:'40px',
      '& span': {
        marginRight: '13px',
      },
      '&:hover': {
        backgroundColor: theme.palette.components.buttonDropdown.onHover.backgroundColor,
      }
    },
    '& .MuiMenuItem-root+.MuiDivider-root': {
      margin: 0,
    },
    '& .Mui-selected': {
      backgroundColor: theme.palette.components.buttonDropdown.selected.backgroundColor,
      
    },
    
  },
}));

export const TaskEntityNavigation = ({ defaultValues }: { defaultValues: any }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const taskEntity = useSelector(selectTaskEntity);
  const userInfo = useSelector(selectUser).userInfo;
  const { setValue } = useFormContext();
  const [open, setOpen] = useState(false);
  const filterPreferences = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === 'GlobalSearchGrid-tasks');
  });

  const returnBtnTitle = () => {
    switch (taskEntity) {
      case TaskEntity.ALL:
        return t('notification.taskManagement.menu.allTasks');
      case TaskEntity.CANDIDATES:
        return (
          t('notification.taskManagement.menu.groupByLabel') + t('notification.taskManagement.menu.candidateTasks')
        );
      case TaskEntity.PLACEMENTS:
        return (
          t('notification.taskManagement.menu.groupByLabel') + t('notification.taskManagement.menu.placementTasks')
        );
      case TaskEntity.FACILITIES:
        return t('notification.taskManagement.menu.groupByLabel') + t('notification.taskManagement.menu.facilityTasks');
      case TaskEntity.ORDERS:
        return t('notification.taskManagement.menu.groupByLabel') + t('notification.taskManagement.menu.orderTasks');
      default:
        return t('notification.taskManagement.menu.allTasks');
    }
  };

  const applyDefaultvalues = () => {
    Object.entries(defaultValues).map(item => setValue(item[0], item[1]));
    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(defaultValues),
        selectedChipFilters: {
          ...defaultTaskFilters,
          assignedToIds: [userInfo?.employeeId],
        },
      }),
    );
  };

  const onClickFxn = entity => {
    dispatch(taskDetailsActions.setTaskEntity(entity));
    if (filterPreferences?.value && entity === TaskEntity.ALL) {
      const selectedFilterName = filterPreferences?.value?.selectedFilter?.name;
      const filterSet = filterPreferences?.value?.filterAttributes?.filterSet?.find(
        item => item.name === selectedFilterName,
      );
      if (filterSet) {
        const selectedFilter = {
          name: filterSet?.name,
          value: filterSet?.value,
          isDefault: filterSet?.isDefault,
        };
        const selectedFilterValue = formatSelectedFilter(selectedFilter?.value);
        const selectedFilters = convertArrayToKeyValuePairs(selectedFilterValue);
        dispatch(gridStateActions.setSelectedFilters(selectedFilters));

        const selectedFilterChips = convertSelectedFilterValue(selectedFilter?.value);
        dispatch(
          gridStateActions?.setFilterAttributes({
            filterObject: selectedFilterValue,
            selectedChipFilters: selectedFilterChips,
          }),
        );
      } else {
        applyDefaultvalues();
      }
    } else {
      applyDefaultvalues();
    }

    setOpen(false);
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
      }}
    >
      <div style={{ position: 'relative' }}>
        <Button
          aria-controls={open ? 'task-entity-navigation' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          variant="contained"
          disableElevation
          onClick={() => {
            setOpen(o => !o);
          }}
          className={classes.btn}
          endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          color="primary"
        >
          {returnBtnTitle()}
        </Button>
        {open && (
          <List className={classes.menuList}>
            <MenuItem
              selected={taskEntity === TaskEntity.ALL}
              onClick={() => {
                onClickFxn(TaskEntity.ALL);
              }}
            >
              <span>
                <img src={AllIcon} alt="All" />
              </span>
              {t('notification.taskManagement.menu.allTasks')}
            </MenuItem>
            <Divider />
            <div className={classes.label}>{t('notification.taskManagement.menu.groupByLabel')}</div>
            <Divider />
            <MenuItem
              selected={taskEntity === TaskEntity.CANDIDATES}
              onClick={() => {
                onClickFxn(TaskEntity.CANDIDATES);
              }}
            >
              <span>
                <img src={CandidateIcon} alt="Candidate" />
              </span>
              {t('notification.taskManagement.menu.candidateTasks')}
            </MenuItem>
            <MenuItem
              selected={taskEntity === TaskEntity.PLACEMENTS}
              onClick={() => {
                onClickFxn(TaskEntity.PLACEMENTS);
              }}
            >
              <span>
                <img src={PlacementIcon} alt="Placement" />
              </span>
              {t('notification.taskManagement.menu.placementTasks')}
            </MenuItem>
            <MenuItem
              selected={taskEntity === TaskEntity.FACILITIES}
              onClick={() => {
                onClickFxn(TaskEntity.FACILITIES);
              }}
            >
              <span>
                <img src={FacilityIcon} alt="Facility" />
              </span>
              {t('notification.taskManagement.menu.facilityTasks')}
            </MenuItem>
            <MenuItem
              selected={taskEntity === TaskEntity.ORDERS}
              onClick={() => {
                onClickFxn(TaskEntity.ORDERS);
              }}
            >
              <span>
                <img src={OrderIcon} alt="Order" />
              </span>
              {t('notification.taskManagement.menu.orderTasks')}
            </MenuItem>
          </List>
        )}
      </div>
    </ClickAwayListener>
  );
};
